import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, ShoppingCart, ClipboardCheck, FileText, Truck, Factory, ArrowRight } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);

function Card({ title, icon: Icon, tone, count, to, children, testId }) {
  const tones = {
    red: "border-red-200 bg-red-50/50 text-red-700",
    amber: "border-amber-200 bg-amber-50/50 text-amber-700",
    blue: "border-slate-200 bg-white text-[#002FA7]",
    green: "border-emerald-200 bg-emerald-50/40 text-emerald-700",
  };
  return (
    <div className={`border ${count ? tones[tone] : "border-slate-200 bg-white text-slate-400"} p-4`} data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.1em]">
          <Icon className="h-4 w-4" /> {title}
        </span>
        <span className="font-heading text-2xl font-bold tabular-nums">{count}</span>
      </div>
      <div className="mt-2 space-y-1 text-xs text-slate-600">{children}</div>
      <Link to={to} className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[#002FA7] hover:underline" data-testid={`${testId}-link`}>
        Open <ArrowRight className="h-3 w-3" />
      </Link>
    </div>
  );
}

const Line = ({ children }) => <p className="truncate">{children}</p>;

export default function OperationsDashboard() {
  const [data, setData] = useState(null);
  useEffect(() => {
    Promise.all([
      api.get("/sales-orders").catch(() => ({ data: [] })),
      api.get("/manufacturing-orders").catch(() => ({ data: [] })),
      api.get("/purchase-orders").catch(() => ({ data: [] })),
      api.get("/delivery-orders").catch(() => ({ data: [] })),
      api.get("/copacker-stock").catch(() => ({ data: [] })),
      api.get("/copacker-fg").catch(() => ({ data: [] })),
    ]).then(([so, mo, po, dos, cps, cpf]) => setData({ so: so.data, mo: mo.data, po: po.data, dos: dos.data, cps: cps.data, cpf: cpf.data }));
  }, []);

  const view = useMemo(() => {
    if (!data) return null;
    const t = today();
    const openSos = data.so.filter((s) => s.status === "open");
    const atRisk = openSos.filter((s) => (s.lines || []).some((l) => (l.shortage_cases || 0) > 0.01 && !l.no_auto_mo));
    const readyToShip = openSos.filter((s) => (s.lines || []).length && (s.lines || []).every((l) => (l.shortage_cases || 0) <= 0.01 || l.no_auto_mo));
    const activeMos = data.mo.filter((m) => !["Completed", "Cancelled"].includes(m.status));
    const overdue = activeMos.filter((m) => m.production_date && m.production_date < t);
    const dueSoon = activeMos.filter((m) => m.production_date && m.production_date >= t && m.production_date <= new Date(Date.now() + 3 * 864e5).toISOString().slice(0, 10));
    const incoming = data.po.filter((p) => ["placed", "in_production", "in_transit", "arrived"].includes(p.status));
    const arrived = incoming.filter((p) => p.status === "arrived");
    const shipmentsToday = data.dos.filter((d) => d.pickup_date === t && d.status !== "completed");
    const negatives = [
      ...data.cps.filter((r) => (r.qty || 0) < -0.01).map((r) => ({ name: `${r.co_packer_name} · ${r.label}`, v: r.qty })),
      ...data.cpf.filter((r) => (r.cases || 0) < -0.01).map((r) => ({ name: `${r.co_packer_name} · ${r.product_name}`, v: r.cases })),
    ];
    return { atRisk, readyToShip, overdue, dueSoon, incoming, arrived, shipmentsToday, negatives, openSos };
  }, [data]);

  if (!view) return <main className="flex min-h-[60vh] items-center justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></main>;

  return (
    <main className="mx-auto max-w-[1200px] px-6 py-8 md:px-10 md:py-12" data-testid="ops-dashboard">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Command Center</p>
      <h1 className="mt-1 font-heading text-4xl font-bold tracking-tighter text-slate-900 sm:text-5xl">Dashboard</h1>
      <p className="mt-2 text-sm text-slate-500">What needs attention today — every card links to where you fix it.</p>

      <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Card title="Sales Orders At Risk" icon={ShoppingCart} tone="red" count={view.atRisk.length} to="/sales-orders" testId="dash-at-risk">
          {view.atRisk.slice(0, 3).map((s) => <Line key={s.id}>{s.so_number} — {s.customer_name} (shortage)</Line>)}
          {!view.atRisk.length && <Line>No open orders with unresolved shortages</Line>}
        </Card>
        <Card title="Production Overdue" icon={ClipboardCheck} tone="red" count={view.overdue.length} to="/production-planning" testId="dash-overdue">
          {view.overdue.slice(0, 3).map((m) => <Line key={m.id}>{m.mwo_number || m.id.slice(0, 6)} — {m.product_name} ({m.production_date})</Line>)}
          {!view.overdue.length && <Line>Nothing past its scheduled date</Line>}
        </Card>
        <Card title="Production Next 3 Days" icon={ClipboardCheck} tone="amber" count={view.dueSoon.length} to="/production-planning" testId="dash-due-soon">
          {view.dueSoon.slice(0, 3).map((m) => <Line key={m.id}>{m.production_date} — {m.product_name}</Line>)}
          {!view.dueSoon.length && <Line>No runs scheduled in the next 3 days</Line>}
        </Card>
        <Card title="POs Awaiting Receipt" icon={FileText} tone="amber" count={view.incoming.length} to="/purchase-orders" testId="dash-incoming">
          {view.arrived.length > 0 && <Line><b>{view.arrived.length} arrived</b> — ready to receive into inventory</Line>}
          {view.incoming.slice(0, 2).map((p) => <Line key={p.id}>{p.po_number} — {p.supplier_name} ({p.status.replace("_", " ")})</Line>)}
          {!view.incoming.length && <Line>No open purchase orders</Line>}
        </Card>
        <Card title="Shipments Today" icon={Truck} tone="blue" count={view.shipmentsToday.length} to="/delivery-orders" testId="dash-shipments">
          {view.shipmentsToday.slice(0, 3).map((d) => <Line key={d.id}>{d.do_number} — {d.customer_name || d.partner_name}</Line>)}
          {!view.shipmentsToday.length && <Line>No pickups scheduled today</Line>}
        </Card>
        <Card title="Negative Co-Packer Balances" icon={Factory} tone="red" count={view.negatives.length} to="/copacker-inventory" testId="dash-negatives">
          {view.negatives.slice(0, 3).map((n, i) => <Line key={i}>{n.name}: {Math.round(n.v).toLocaleString()}</Line>)}
          {!view.negatives.length && <Line>All co-packer balances are healthy</Line>}
        </Card>
        <Card title="Ready To Ship" icon={Truck} tone="green" count={view.readyToShip.length} to="/sales-orders" testId="dash-ready">
          {view.readyToShip.slice(0, 3).map((s) => <Line key={s.id}>{s.so_number} — {s.customer_name}{s.ship_date ? ` · RDD ${s.ship_date}` : ""}</Line>)}
          {!view.readyToShip.length && <Line>No fully-covered open orders</Line>}
        </Card>
        <Card title="Open Sales Orders" icon={ShoppingCart} tone="blue" count={view.openSos.length} to="/sales-orders" testId="dash-open-sos">
          {view.openSos.slice(0, 3).map((s) => <Line key={s.id}>{s.so_number} — {s.customer_name}</Line>)}
          {!view.openSos.length && <Line>Order book is clear</Line>}
        </Card>
      </div>
    </main>
  );
}
